import { REJECTED_STATUSES } from 'components/VenueTile/VenueCard';
import { ACTION_LABELS, SHORTLIST_LIMIT_REACHED_LABEL, VenueActions } from 'constants/venueActions';
import { INQUIRY_REJECTED_STATUS, VenueStatus } from 'constants/venueStatus';
import { VENUE_ID_CORPORATE_OFFICE } from 'constants/venueTypes';
import { keyBy } from 'lodash';
import { MAX_INQUIRY_VENUE_SLOTS, MAX_QUEUE_VENUE_SLOTS } from './constants';
import { VenueActionState } from './types';
import { getValidInquiryVenues, getVenueId } from './venue';

/**
 * Merges venue search results with inquiry venue statuses.
 * Only overwrites status for rejected venues to maintain inquiry state.
 *
 * @param venues - Venues from search results or API
 * @param inquiryVenues - Venues from current inquiry with statuses
 * @returns Venues with inquiry statuses merged for rejected venues
 */
export const mergeVenuesWithInquiryStatus = (venues: Bizly.Venue[], inquiryVenues: Bizly.Venue[]): Bizly.Venue[] => {
    if (!venues?.length) return [];
    if (!inquiryVenues?.length) return venues;

    const inquiryVenuesById = keyBy(inquiryVenues, v => getVenueId(v));

    return venues.map(venue => {
        const inquiryVenue = inquiryVenuesById[getVenueId(venue)];
        if (inquiryVenue?.status && REJECTED_STATUSES.has(inquiryVenue.status)) {
            return { ...venue, status: inquiryVenue.status };
        }
        return venue;
    });
};

/**
 * Maps venue status to venue action for inquiry venues
 */
const getInquiryAction = (status: Bizly.Venue['status']): VenueActions => {
    switch (status) {
        case VenueStatus.Added:
            return VenueActions.REMOVE_FROM_SHORTLIST;
        case VenueStatus.Queued:
            return VenueActions.REMOVE_FROM_QUEUE;
        case VenueStatus.InquiryCancelled:
            return VenueActions.RESUBMIT_INQUIRY;
        case VenueStatus.Submitted:
            return VenueActions.VIEW_INQUIRY;
        case VenueStatus.ProposalReceived:
            return VenueActions.VIEW_PROPOSAL;
        default:
            return VenueActions.NONE;
    }
};

/**
 * Creates a base venue action state. statusMessage is derived from action (or override for NONE).
 */
const createBaseState = (
    action: VenueActions,
    canAddOrRemove: boolean,
    isInInquiry: boolean,
    actionDisabled: boolean,
    status: Bizly.Venue['status'] | null,
    venue?: Bizly.Venue,
    statusMessageOverride?: string
): VenueActionState => ({
    action,
    canAddOrRemove,
    isInInquiry,
    actionDisabled,
    statusMessage: statusMessageOverride ?? ACTION_LABELS[action],
    status,
    ...(venue && { venue }),
});

/**
 * Handles venue state when it's in the current inquiry
 */
const handleVenueInInquiry = (inquiryVenue: Bizly.Venue, effectiveStatus: Bizly.Venue['status']): VenueActionState => {
    const canAddOrRemove =
        effectiveStatus === VenueStatus.Added ||
        effectiveStatus === VenueStatus.InquiryCancelled ||
        effectiveStatus === VenueStatus.Queued;
    const actionDisabled = INQUIRY_REJECTED_STATUS.has(effectiveStatus);
    const action = getInquiryAction(effectiveStatus);
    const statusMessageOverride = action === VenueActions.NONE ? effectiveStatus || undefined : undefined;

    return createBaseState(
        action,
        canAddOrRemove,
        true, // isInInquiry
        actionDisabled,
        effectiveStatus,
        inquiryVenue,
        statusMessageOverride
    );
};

/**
 * Handles venue state when it's not in the current inquiry
 */
const handleVenueNotInInquiry = (isInquirySubmitted: boolean): VenueActionState => {
    const action = isInquirySubmitted ? VenueActions.ADD_TO_INQUIRY : VenueActions.ADD_TO_SHORTLIST;

    return createBaseState(action, true, false, false, null);
};

type VenueActionStateProps = {
    venue: Bizly.Venue;
    inquiryVenues: Bizly.Venue[];
    currentInquiry?: BizlyAPI.Inquiry | null;
    queuedVenuesCount?: number;
    queuedVenueIds?: number[];
    availableQueueSlots?: number | null;
    /** When false (legacy /event planner), shortlist cap applies but queue add/remove UX is hidden. */
    venueQueueEnabled?: boolean;
};

/** Actions that attach the venue to the inquiry, which the API refuses for a venue outside the team's countries. */
const ATTACHING_ACTIONS = new Set<VenueActions>([
    VenueActions.ADD_TO_SHORTLIST,
    VenueActions.ADD_TO_INQUIRY,
    VenueActions.ADD_TO_QUEUE,
    VenueActions.RE_ADD_TO_QUEUE,
    VenueActions.RESUBMIT_INQUIRY,
]);

/**
 * Mirrors the API's Team::isVenueCountryAllowed: a venue with no country is allowed, and a missing or
 * empty list is treated as unrestricted (the API lists every country for an unrestricted team).
 */
export const isVenueOutsideTeamRegion = (venue: Bizly.Venue, enabledCountries?: Bizly.Country[]): boolean => {
    const countryId = venue.country?.id;
    if (countryId == null || !enabledCountries?.length) return false;
    return !enabledCountries.some(country => country.id === countryId);
};

/**
 * Determines the current state and available actions for a venue
 *
 * @param venue - The venue to check
 * @param inquiryVenues - Optional array of current inquiry venues
 * @param enabledCountries - The team's enabled countries; attaching a venue outside them is disabled
 * @returns VenueActionState with current state and available actions
 */
export const getEventVenueActionData = ({
    enabledCountries,
    ...props
}: VenueActionStateProps & { enabledCountries?: Bizly.Country[] }): VenueActionState => {
    const state = getVenueActionState(props);
    if (ATTACHING_ACTIONS.has(state.action) && isVenueOutsideTeamRegion(props.venue, enabledCountries)) {
        return createBaseState(VenueActions.OUT_OF_REGION, false, state.isInInquiry, true, state.status, state.venue);
    }
    return state;
};

const getVenueActionState = (props: VenueActionStateProps): VenueActionState => {
    const {
        venue,
        inquiryVenues,
        currentInquiry,
        queuedVenuesCount = 0,
        queuedVenueIds = [],
        availableQueueSlots,
        venueQueueEnabled = true,
    } = props;

    // Early return for invalid venue
    if (!venue) {
        return createBaseState(VenueActions.INVALID, false, false, true, null);
    }

    // Early return for temporarily closed venues
    if (venue.tempClosed) {
        return createBaseState(VenueActions.TEMP_CLOSED, false, false, true, null);
    }

    // Calculate inquiry constraints (shortlist = non-queued only; queued venues live in queue section)
    const validInquiryVenues = getValidInquiryVenues(inquiryVenues);
    const shortlistVenues = validInquiryVenues.filter(v => v.status !== VenueStatus.Queued);
    const hasMaxShortlist = shortlistVenues.length >= MAX_INQUIRY_VENUE_SLOTS;
    const hasMaxQueue =
        availableQueueSlots != null
            ? availableQueueSlots === 0 || queuedVenuesCount >= availableQueueSlots
            : queuedVenuesCount >= MAX_QUEUE_VENUE_SLOTS;
    const cannotAddToQueue = !venueQueueEnabled || hasMaxQueue;
    const inquiryVenue = inquiryVenues.find(v => getVenueId(v) === getVenueId(venue));
    const isInInquiry = !!inquiryVenue;
    const effectiveStatus = inquiryVenue?.status || venue.status;

    const isInquirySubmitted = !!currentInquiry?.submittedAt;
    /**
     * "Max Inquiries Reached" only after the inquiry is submitted. Draft / no record still has venue rows in state;
     * use shortlist wording until submit so browse matches user expectation.
     */
    const maxReachedStatusMessage = isInquirySubmitted ? undefined : SHORTLIST_LIMIT_REACHED_LABEL;
    const isCorporateOffice = venue.type?.id === VENUE_ID_CORPORATE_OFFICE;

    // No snake_case fallback: API client (src/api/index.js) camelizes all responses.
    const isCancelledFromQueue =
        effectiveStatus === VenueStatus.InquiryCancelled && !!(inquiryVenue?.queuedAt ?? venue.queuedAt);

    // True when the venue was previously in the queue for this inquiry but is no longer queued.
    const wasInQueue = !!(inquiryVenue?.queuedAt ?? venue.queuedAt) && effectiveStatus !== VenueStatus.Queued;

    // Handle venues that are already in the shortlist first (show "Remove from short list", etc.)
    // Exception: Inquiry Cancelled + queuedAt = was removed from queue after submit → show "Add to Queue" / "Add to short list"
    if (isInInquiry && !isCancelledFromQueue) {
        const inquiryState = handleVenueInInquiry(inquiryVenue!, effectiveStatus);
        // For corporate offices, preserve inquiry state but keep button disabled
        if (isCorporateOffice) {
            return createBaseState(
                inquiryState.action,
                false, // canAddOrRemove - disabled for corporate offices
                true, // isInInquiry - preserve actual state
                true, // actionDisabled - always disabled for corporate offices
                effectiveStatus,
                inquiryVenue
            );
        }
        // Inquiry Cancelled with no queuedAt: if shortlist is full show "Add to Queue" or "Remove from queue" depending on queue membership
        if (effectiveStatus === VenueStatus.InquiryCancelled && hasMaxShortlist) {
            if (cannotAddToQueue && !queuedVenueIds.includes(getVenueId(venue))) {
                return createBaseState(
                    VenueActions.MAX_REACHED,
                    false,
                    true,
                    true,
                    effectiveStatus,
                    inquiryVenue,
                    maxReachedStatusMessage
                );
            }
            if (queuedVenueIds.includes(getVenueId(venue))) {
                return createBaseState(
                    VenueActions.REMOVE_FROM_QUEUE,
                    true,
                    true,
                    false,
                    VenueStatus.Queued,
                    inquiryVenue
                );
            }
            // Corporate offices show "Add to Queue" but button stays disabled (cannot be added to queue)
            if (isCorporateOffice) {
                return createBaseState(VenueActions.ADD_TO_QUEUE, false, true, true, effectiveStatus, inquiryVenue);
            }
            const action = wasInQueue ? VenueActions.RE_ADD_TO_QUEUE : VenueActions.ADD_TO_QUEUE;
            return createBaseState(action, true, true, false, effectiveStatus, inquiryVenue);
        }
        return inquiryState;
    }

    // Venue is NOT in shortlist (or was cancelled from queue). If shortlist is full: disable only if queue is full; otherwise "Add to Queue"
    if (hasMaxShortlist) {
        if (cannotAddToQueue) {
            return createBaseState(
                VenueActions.MAX_REACHED,
                false,
                false,
                true,
                null,
                undefined,
                maxReachedStatusMessage
            );
        }
        // Corporate offices show "Add to Queue" but button stays disabled (cannot be added to queue)
        if (isCorporateOffice) {
            return createBaseState(VenueActions.ADD_TO_QUEUE, false, false, true, null);
        }
        const action = wasInQueue ? VenueActions.RE_ADD_TO_QUEUE : VenueActions.ADD_TO_QUEUE;
        return createBaseState(action, true, false, false, null);
    }

    // Handle venues not in inquiry (shortlist has room)
    // Check for corporate locations - cannot be added to shortlist (button disabled)
    if (isCorporateOffice) {
        const action = isInquirySubmitted ? VenueActions.ADD_TO_INQUIRY : VenueActions.ADD_TO_SHORTLIST;
        return createBaseState(action, false, false, true, null);
    }

    return handleVenueNotInInquiry(isInquirySubmitted);
};
